import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAdminData } from '../../admin/AdminDataContext';
import { ApiError } from '../../lib/api';
import { featureFlags } from '../../lib/featureFlags';
import { formatCellValue, inputPropsFor } from '../../lib/fieldFormat';
import { computeGeometryMetrics, formatArea, formatLength, formatLonLat, pickRepresentativePoint } from '../../lib/geometryMetrics';
import { layersApi } from '../../lib/layersApi';
import { DeleteSelectionIcon, ExportSelectionIcon, ShareIcon, TerritoryCalculatorIcon } from '../icons/MapToolIcons';
import { FeatureAttachmentsSection } from './FeatureAttachmentsSection';
import { HierarchyModal } from './HierarchyModal';
import { InventoryReportModal } from './InventoryReportModal';
import { ParentObjectPicker } from './ParentObjectPicker';
import { PhotoCarousel } from './PhotoCarousel';
import { RelatedRowsSection } from './RelatedRowsSection';
import { geometryTypeLabels, pickObjectTitle, useObjectCard } from './useObjectCard';

function HeaderIconButton({
  title,
  active,
  disabled,
  blurred,
  onClick,
  children,
}: {
  title: string;
  active?: boolean;
  disabled?: boolean;
  blurred?: boolean;
  onClick?: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      disabled={disabled}
      className={`flex size-9 items-center justify-center rounded-lg transition-colors ${
        active ? 'bg-action-primary text-text-inverse' : 'text-text-secondary hover:bg-bg-surface-secondary hover:text-text-primary'
      } ${disabled ? 'cursor-not-allowed opacity-50' : ''} ${blurred ? 'blur-[1.5px]' : ''}`}
    >
      {children}
    </button>
  );
}

function MetricRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="shrink-0 text-xs text-text-secondary">{label}</span>
      <span className="truncate text-xs font-medium text-text-primary">{value}</span>
    </div>
  );
}

/** The "big popup" variant, opened from the map's selection popup — a public-
 * facing presentation of the object (centered photo carousel, quick actions
 * in the header) rather than the dense admin table-style layout of the full
 * page (see ObjectCardBody/ObjectCardPage, still opened from the layer
 * table). Shares only the data/editing hook, not the layout. */
export function ObjectCardModal({
  layerId,
  featureId,
  onClose,
}: {
  layerId: string;
  featureId: string;
  onClose: () => void;
}) {
  const [currentFeatureId, setCurrentFeatureId] = useState(featureId);
  const card = useObjectCard(layerId, currentFeatureId, setCurrentFeatureId);
  const { layer, feature } = card;
  const { mapSettings } = useAdminData();
  const [parentPickerOpen, setParentPickerOpen] = useState(false);
  const [childPickerOpen, setChildPickerOpen] = useState(false);
  const [addChildError, setAddChildError] = useState<string | null>(null);
  const [addingChild, setAddingChild] = useState(false);
  const [removingChildId, setRemovingChildId] = useState<string | null>(null);
  const [hierarchyModalOpen, setHierarchyModalOpen] = useState(false);
  const [inventoryReportOpen, setInventoryReportOpen] = useState(false);

  const isPointGeometry = feature?.geometry.type === 'Point';

  const handleRemoveChild = async (child: { id: string; layer_id: string }) => {
    setRemovingChildId(child.id);
    setAddChildError(null);
    try {
      await layersApi.setFeatureParent(child.layer_id, child.id, null);
      card.refreshChildren();
    } catch (err) {
      setAddChildError(err instanceof ApiError ? err.message : 'Не удалось отвязать дочерний объект.');
    } finally {
      setRemovingChildId(null);
    }
  };

  const handleAddChild = async (childFeatureId: string, childLayerId: string, snapToParent: boolean) => {
    setAddingChild(true);
    setAddChildError(null);
    try {
      await layersApi.setFeatureParent(childLayerId, childFeatureId, feature!.id, snapToParent);
      card.refreshChildren();
      setChildPickerOpen(false);
    } catch (err) {
      setAddChildError(err instanceof ApiError ? err.message : 'Не удалось привязать дочерний объект.');
    } finally {
      setAddingChild(false);
    }
  };

  const handleCreateChild = async (childLayerId: string) => {
    if (!feature) return;
    const point = pickRepresentativePoint(feature.geometry);
    if (!point) {
      setAddChildError('Не удалось определить координаты для нового объекта.');
      return;
    }
    setAddingChild(true);
    setAddChildError(null);
    try {
      const created = await layersApi.createFeature(childLayerId, { type: 'Point', coordinates: point });
      await layersApi.setFeatureParent(childLayerId, created.id, feature.id, true);
      card.refreshChildren();
      setChildPickerOpen(false);
    } catch (err) {
      setAddChildError(err instanceof ApiError ? err.message : 'Не удалось создать дочерний объект.');
    } finally {
      setAddingChild(false);
    }
  };

  const [calculatorOpen, setCalculatorOpen] = useState(false);
  const [sharePanelOpen, setSharePanelOpen] = useState(false);
  const [shared, setShared] = useState(false);

  const metrics = useMemo(() => (feature ? computeGeometryMetrics(feature.geometry) : null), [feature]);

  const handleCopyLink = async () => {
    try {
      const url = new URL(`/app/data/layers/${layerId}/objects/${currentFeatureId}`, window.location.origin);
      await navigator.clipboard.writeText(url.toString());
      setShared(true);
      setTimeout(() => setShared(false), 1500);
    } catch {
      // буфер обмена недоступен (например, без HTTPS) — тихо игнорируем
    }
  };

  const handleClose = () => card.confirmDiscardIfNeeded() && onClose();

  const handleDelete = () => card.handleDelete(onClose);

  const siblingIndex = feature ? card.siblingFeatures.findIndex((f) => f.id === feature.id) : -1;
  const hasSiblingNav = card.siblingFeatures.length > 1 && siblingIndex !== -1;
  const goPrev = () => siblingIndex > 0 && card.goToFeature(card.siblingFeatures[siblingIndex - 1].id);
  const goNext = () =>
    siblingIndex < card.siblingFeatures.length - 1 && card.goToFeature(card.siblingFeatures[siblingIndex + 1].id);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/52 p-6">
      <div className="flex h-[85vh] w-full max-w-[900px] flex-col overflow-hidden rounded-2xl border border-border-default bg-bg-surface shadow-2xl">
        <header className="flex shrink-0 items-center gap-3 border-b border-border-default px-5 py-3">
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-[16px] font-semibold text-text-primary">
              {feature ? pickObjectTitle(feature.properties) : '…'}
            </h1>
            {feature && (
              <p className="mt-0.5 truncate text-[11px] text-text-secondary">
                {geometryTypeLabels[feature.geometry.type] ?? feature.geometry.type}
                {' · обновлено '}
                {new Date(feature.updated_at).toLocaleDateString('ru-RU')}
                {feature.updated_by_name ? ` · ${feature.updated_by_name}` : ''}
              </p>
            )}
          </div>

          <div className="flex shrink-0 items-center gap-1">
            {card.saveError && <p className="max-w-[200px] text-xs font-medium text-status-danger">{card.saveError}</p>}
            {card.pendingCount > 0 && (
              <>
                <span className="text-[11px] text-text-secondary">Изменено: {card.pendingCount}</span>
                <button
                  type="button"
                  onClick={card.handleDiscard}
                  disabled={card.saving}
                  className="px-2 text-[13px] font-semibold text-text-secondary hover:text-text-primary"
                >
                  Отменить
                </button>
                <button
                  type="button"
                  onClick={card.handleSaveAll}
                  disabled={card.saving}
                  className="mr-1 flex h-9 items-center rounded-lg bg-action-primary px-3 text-[13px] font-medium text-text-inverse hover:bg-action-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {card.saving ? 'Сохраняем…' : 'Сохранить'}
                </button>
              </>
            )}

            <HeaderIconButton title="Объектный калькулятор" active={calculatorOpen} onClick={() => setCalculatorOpen((v) => !v)}>
              <TerritoryCalculatorIcon className="size-[18px]" />
            </HeaderIconButton>

            <HeaderIconButton title="Экспорт отчёта — скоро" disabled blurred>
              <ExportSelectionIcon className="size-[18px]" />
            </HeaderIconButton>

            <div className="relative">
              <HeaderIconButton title="Поделиться" active={sharePanelOpen} onClick={() => setSharePanelOpen((v) => !v)}>
                <ShareIcon className="size-[18px]" />
              </HeaderIconButton>
              {sharePanelOpen && (
                <div className="absolute right-0 top-full z-10 mt-2 w-64 rounded-xl border border-border-default bg-bg-surface p-3 shadow-lg">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-xs font-semibold text-text-primary">Поделиться объектом</p>
                    <button
                      type="button"
                      onClick={() => setSharePanelOpen(false)}
                      className="flex size-6 shrink-0 items-center justify-center rounded-md text-text-secondary hover:bg-bg-surface-secondary"
                    >
                      ✕
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={handleCopyLink}
                    className="mt-2.5 flex h-9 w-full items-center justify-center rounded-lg bg-action-primary text-[13px] font-medium text-text-inverse hover:bg-action-primary-hover"
                  >
                    {shared ? 'Ссылка скопирована ✓' : 'Скопировать ссылку'}
                  </button>
                </div>
              )}
            </div>

            {card.canEdit && (
              <HeaderIconButton title="Удалить объект" onClick={handleDelete} disabled={card.deleting}>
                <DeleteSelectionIcon className="size-[18px]" />
              </HeaderIconButton>
            )}

            <button
              type="button"
              onClick={handleClose}
              title="Закрыть"
              className="ml-1 flex size-9 shrink-0 items-center justify-center rounded-lg text-xl font-semibold text-text-secondary hover:bg-bg-surface-secondary hover:text-text-primary"
            >
              ×
            </button>
          </div>
        </header>

        {card.loading ? (
          <p className="p-8 text-sm text-text-secondary">Загрузка…</p>
        ) : card.loadError || !layer || !feature ? (
          <p className="p-8 text-sm font-medium text-status-danger">{card.loadError ?? 'Объект не найден'}</p>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto">
              <div className="mx-auto w-full max-w-[640px] px-6 py-6">
                <PhotoCarousel photoId={feature.photo_file_id} canEdit={card.canEdit} onChange={card.handleSetPhoto} />

                {calculatorOpen && (
                  <div className="mt-4 flex flex-col gap-2 rounded-xl bg-bg-surface-secondary p-4">
                    <p className="text-[11px] font-semibold text-text-primary">Измерения объекта</p>
                    {!metrics ? (
                      <p className="text-[11px] text-text-secondary">Измерения недоступны для этой геометрии.</p>
                    ) : metrics.kind === 'point' ? (
                      <MetricRow label="Координаты" value={formatLonLat([metrics.lon, metrics.lat])} />
                    ) : metrics.kind === 'line' ? (
                      <>
                        <MetricRow label="Длина" value={formatLength(metrics.lengthM)} />
                        <MetricRow label="Начальная точка" value={formatLonLat(metrics.start)} />
                        <MetricRow label="Конечная точка" value={formatLonLat(metrics.end)} />
                      </>
                    ) : (
                      <>
                        <MetricRow label="Площадь" value={formatArea(metrics.areaM2)} />
                        <MetricRow label="Периметр" value={formatLength(metrics.perimeterM)} />
                        <div className="flex flex-col gap-1">
                          <span className="text-xs text-text-secondary">Поворотные точки ({metrics.vertices.length})</span>
                          <div className="flex max-h-20 flex-col gap-0.5 overflow-y-auto">
                            {metrics.vertices.map((v, i) => (
                              <span key={i} className="text-[11px] font-medium text-text-primary">
                                {i + 1}. {formatLonLat(v)}
                              </span>
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}

                <section className="mt-5">
                  <h2 className="text-sm font-semibold text-text-primary">Атрибуты</h2>
                  {!card.canEdit && (
                    <p className="mt-1 text-xs text-text-secondary">У вас нет доступа к редактированию этого объекта</p>
                  )}

                  {layer.fields.length === 0 ? (
                    <p className="mt-3 text-xs text-text-secondary">У слоя нет атрибутивных полей</p>
                  ) : (
                    <div className="mt-3 flex flex-col divide-y divide-border-default">
                      {layer.fields.map((field) => {
                        const isEditing = card.editingCode === field.code;
                        const value = card.effectiveValue(field.code);
                        const isDirty = field.code in card.pending;

                        return (
                          <div key={field.code} className="grid grid-cols-[160px_1fr] items-start gap-3 py-2">
                            <label className="pt-2 text-[12px] font-medium text-text-secondary">{field.name}</label>

                            {field.data_type === 'boolean' ? (
                              <div className={`rounded-[9px] px-2 py-1.5 ${isDirty ? 'bg-action-primary/10' : ''}`}>
                                <input
                                  type="checkbox"
                                  checked={Boolean(value)}
                                  disabled={!card.canEdit}
                                  onChange={(e) => card.stageValue(field, e.target.checked)}
                                  className="size-[18px] rounded-[5px] accent-action-primary disabled:opacity-40"
                                />
                              </div>
                            ) : field.data_type === 'dictionary' ? (
                              (() => {
                                const dictId = field.dictionary_id ?? undefined;
                                const dictValues = dictId ? card.dictValuesByDict[dictId] : undefined;
                                const valueId = typeof value === 'string' ? value : null;
                                const matched = dictValues?.find((v) => v.id === valueId);
                                return isEditing ? (
                                  <select
                                    autoFocus
                                    value={valueId ?? ''}
                                    onChange={(e) => {
                                      card.stageValue(field, e.target.value || null);
                                      card.setEditingCode(null);
                                    }}
                                    onBlur={() => card.setEditingCode(null)}
                                    className="h-9 w-full rounded-[9px] border border-action-primary bg-bg-surface px-2.5 text-[13px] text-text-primary outline-none"
                                  >
                                    <option value="">—</option>
                                    {(dictValues ?? []).map((v) => (
                                      <option key={v.id} value={v.id}>
                                        {v.name}
                                      </option>
                                    ))}
                                  </select>
                                ) : (
                                  <div
                                    onClick={() => card.canEdit && card.setEditingCode(field.code)}
                                    className={`rounded-[9px] px-2.5 py-1.5 text-[13px] ${isDirty ? 'bg-action-primary/10' : ''} ${
                                      card.canEdit ? 'cursor-pointer hover:bg-bg-surface-secondary' : ''
                                    }`}
                                  >
                                    {matched ? (
                                      <Link
                                        to={`/app/dictionaries/${dictId}/values/${matched.id}`}
                                        onClick={(e) => e.stopPropagation()}
                                        className="font-medium text-action-primary hover:underline"
                                      >
                                        {matched.name}
                                      </Link>
                                    ) : (
                                      <span className="text-text-primary">—</span>
                                    )}
                                  </div>
                                );
                              })()
                            ) : isEditing ? (
                              <div>
                                <input
                                  autoFocus
                                  {...inputPropsFor(field.data_type)}
                                  value={card.editDraft}
                                  onChange={(e) => {
                                    card.setEditDraft(e.target.value);
                                    card.setEditError(null);
                                  }}
                                  onBlur={() => card.commitTextEdit(field)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') card.commitTextEdit(field);
                                    if (e.key === 'Escape') card.setEditingCode(null);
                                  }}
                                  className={`h-9 w-full rounded-[9px] border bg-bg-surface px-2.5 text-[13px] text-text-primary outline-none ${
                                    card.editError ? 'border-status-danger' : 'border-action-primary'
                                  }`}
                                />
                                {card.editError && <p className="mt-1 text-[11px] font-medium text-status-danger">{card.editError}</p>}
                              </div>
                            ) : (
                              <p
                                onClick={() => card.startEdit(field)}
                                className={`rounded-[9px] px-2.5 py-1.5 text-[13px] text-text-primary ${
                                  isDirty ? 'bg-action-primary/10' : ''
                                } ${card.canEdit ? 'cursor-pointer hover:bg-bg-surface-secondary' : ''}`}
                              >
                                {formatCellValue(value)}
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </section>

                {featureFlags.objectHierarchy && (
                  <section className="mt-5">
                    <div className="flex items-center justify-between">
                      <h2 className="text-sm font-semibold text-text-primary">Иерархия</h2>
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() => setInventoryReportOpen(true)}
                          className="text-[12px] font-medium text-action-primary hover:underline"
                        >
                          Отчёт по инвентаризации
                        </button>
                        <button
                          type="button"
                          onClick={() => setHierarchyModalOpen(true)}
                          className="text-[12px] font-medium text-action-primary hover:underline"
                        >
                          Показать иерархию
                        </button>
                      </div>
                    </div>

                    <div className="mt-3">
                      <p className="text-[12px] font-medium text-text-secondary">Родительский объект</p>
                      {feature.parent ? (
                        <div className="mt-1 flex items-center gap-3">
                          <Link
                            to={`/app/data/layers/${feature.parent.layer_id}/objects/${feature.parent.id}`}
                            className="text-[13px] font-medium text-action-primary hover:underline"
                          >
                            {feature.parent.title}
                          </Link>
                          <span className="text-[11px] text-text-secondary">{feature.parent.layer_name}</span>
                          {card.canEdit && (
                            <button
                              type="button"
                              onClick={() => card.setParent(null)}
                              disabled={card.parentSaving}
                              className="text-[11px] font-medium text-text-secondary hover:text-status-danger disabled:opacity-50"
                            >
                              Убрать
                            </button>
                          )}
                        </div>
                      ) : (
                        <p className="mt-1 text-[13px] text-text-secondary">Не задан</p>
                      )}
                      {card.canEdit && !parentPickerOpen && (
                        <button
                          type="button"
                          onClick={() => setParentPickerOpen(true)}
                          className="mt-1.5 text-[12px] font-medium text-action-primary hover:underline"
                        >
                          {feature.parent ? 'Изменить' : 'Задать родительский объект'}
                        </button>
                      )}
                      {card.parentError && <p className="mt-1 text-[11px] font-medium text-status-danger">{card.parentError}</p>}
                      {parentPickerOpen && mapSettings?.id && (
                        <ParentObjectPicker
                          mapId={mapSettings.id}
                          excludeFeatureId={feature.id}
                          snapCheckboxLabel={isPointGeometry ? 'Переместить этот объект к координатам родителя' : undefined}
                          snapCheckboxEnabledForLayer={() => isPointGeometry}
                          onCancel={() => setParentPickerOpen(false)}
                          onPick={(parentId, _parentLayerId, snapToParent) => {
                            card.setParent(parentId, snapToParent);
                            setParentPickerOpen(false);
                          }}
                        />
                      )}
                    </div>

                    <div className="mt-4">
                      <p className="text-[12px] font-medium text-text-secondary">
                        Дочерние объекты{card.children.length > 0 ? ` (${card.children.length})` : ''}
                      </p>
                      {card.children.length === 0 ? (
                        <p className="mt-1 text-[13px] text-text-secondary">Нет</p>
                      ) : (
                        <div className="mt-1 flex flex-col gap-1">
                          {card.children.map((child) => (
                            <div key={child.id} className="flex items-center gap-3">
                              <Link
                                to={`/app/data/layers/${child.layer_id}/objects/${child.id}`}
                                className="text-[13px] font-medium text-action-primary hover:underline"
                              >
                                {child.title}
                              </Link>
                              <span className="text-[11px] text-text-secondary">{child.layer_name}</span>
                              {card.canEdit && (
                                <button
                                  type="button"
                                  onClick={() => handleRemoveChild(child)}
                                  disabled={removingChildId === child.id}
                                  className="text-[11px] font-medium text-text-secondary hover:text-status-danger disabled:opacity-50"
                                >
                                  {removingChildId === child.id ? 'Отвязываем…' : 'Убрать'}
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                      {card.canEdit && !childPickerOpen && (
                        <button
                          type="button"
                          onClick={() => setChildPickerOpen(true)}
                          className="mt-1.5 text-[12px] font-medium text-action-primary hover:underline"
                        >
                          Добавить дочерний объект
                        </button>
                      )}
                      {addChildError && <p className="mt-1 text-[11px] font-medium text-status-danger">{addChildError}</p>}
                      {childPickerOpen && mapSettings?.id && (
                        <ParentObjectPicker
                          mapId={mapSettings.id}
                          excludeFeatureId={feature.id}
                          snapCheckboxLabel="Переместить дочерний объект к координатам этого"
                          snapCheckboxEnabledForLayer={(l) => l?.geometry_type === 'point'}
                          onCancel={() => setChildPickerOpen(false)}
                          onPick={(childId, childLayerId, snapToParent) => {
                            if (!addingChild) handleAddChild(childId, childLayerId, snapToParent);
                          }}
                          onCreateNew={(childLayerId) => {
                            if (!addingChild) handleCreateChild(childLayerId);
                          }}
                        />
                      )}
                    </div>
                  </section>
                )}

                <section className="mt-5 empty:hidden">
                  <RelatedRowsSection layerId={layerId} featureId={feature.id} featureTitle={pickObjectTitle(feature.properties)} />
                </section>

                <section className="mt-5 empty:hidden">
                  <FeatureAttachmentsSection
                    layerId={layerId}
                    featureId={feature.id}
                    properties={feature.properties}
                    canEdit={card.canEdit}
                    onChanged={card.setFeatureProperties}
                  />
                </section>
              </div>
            </div>

            {hasSiblingNav && (
              <div className="shrink-0 border-t border-border-default bg-bg-surface px-5 py-3">
                <div className="mx-auto flex w-full max-w-[640px] items-center justify-center gap-4">
                  <button
                    type="button"
                    onClick={goPrev}
                    disabled={siblingIndex <= 0}
                    title="Предыдущий объект"
                    className="flex size-9 items-center justify-center rounded-lg border border-border-default text-text-primary hover:bg-bg-surface-secondary disabled:cursor-not-allowed disabled:opacity-30"
                  >
                    ‹
                  </button>
                  <span className="text-xs font-medium text-text-secondary">
                    {siblingIndex + 1} из {card.siblingFeatures.length}
                  </span>
                  <button
                    type="button"
                    onClick={goNext}
                    disabled={siblingIndex >= card.siblingFeatures.length - 1}
                    title="Следующий объект"
                    className="flex size-9 items-center justify-center rounded-lg border border-border-default text-text-primary hover:bg-bg-surface-secondary disabled:cursor-not-allowed disabled:opacity-30"
                  >
                    ›
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {hierarchyModalOpen && feature && (
        <HierarchyModal layerId={layerId} featureId={feature.id} onClose={() => setHierarchyModalOpen(false)} />
      )}
      {inventoryReportOpen && feature && (
        <InventoryReportModal layerId={layerId} featureId={feature.id} onClose={() => setInventoryReportOpen(false)} />
      )}
    </div>
  );
}
